'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from 'firebase/auth';
import { authHelpers } from '../lib/firebase';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { userTrackingApi } from '../lib/apiClient';
import { trackEvent } from '../lib/yandexMetrika';
import { trackVisit, resetVisitCount } from '../lib/visitTracker';
import {
  hashIdentifier,
  resetAmplitudeIdentity,
  setAmplitudeUserId,
  setAmplitudeUserProperties,
  trackAmplitudeEvent,
} from '../lib/analytics';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithYandex: () => Promise<void>;
  signInWithVK: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  setupPassword: (email: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  linkWithGoogle: () => Promise<void>;
  linkWithYandex: () => Promise<void>;
  linkWithVK: () => Promise<void>;
  unlinkProvider: (providerId: string) => Promise<void>;
  getLinkedProviders: () => any[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const previousUserIdRef = useRef<string | null>(null);
  const visitTrackedRef = useRef(false);

  // Track visit on mount
  useEffect(() => {
    if (!visitTrackedRef.current) {
      trackVisit();
      visitTrackedRef.current = true;
    }
  }, []);

  // Helper function to get provider name
  const getProviderName = (user: User): string => {
    if (user.providerData && user.providerData.length > 0) {
      const providerId = user.providerData[0].providerId;
      if (providerId === 'google.com') return 'Google';
      if (providerId === 'oidc.yandex') return 'Yandex';
      if (providerId === 'oidc.vk') return 'VK';
      if (providerId === 'password') return 'Email';
      return providerId;
    }
    return 'Email';
  };

  useEffect(() => {
    let cancelled = false;

    const syncAmplitudeIdentity = async () => {
      if (!user) {
        resetAmplitudeIdentity();
        return;
      }

      const provider = getProviderName(user);
      const providerIds = (user.providerData || [])
        .map((providerData) => providerData?.providerId)
        .filter(Boolean) as string[];
      const hashedUserId = await hashIdentifier(user.uid);

      if (cancelled) {
        return;
      }

      setAmplitudeUserId(`uid_${hashedUserId}`);
      setAmplitudeUserProperties({
        auth_provider: provider.toLowerCase(),
        auth_providers: providerIds,
        is_anonymous: user.isAnonymous,
        locale: i18n.language,
      });
    };

    syncAmplitudeIdentity();

    return () => {
      cancelled = true;
    };
  }, [user, i18n.language]);

  // Get Telegram user ID from Telegram WebApp API or URL parameter
  useEffect(() => {
    const getTelegramUserId = async () => {
      let tgUserId: string | null = null;
      
      // Method 1: Try to get from Telegram WebApp API
      try {
        const telegramWebApp = (window as any).Telegram?.WebApp;
        if (telegramWebApp) {
          try {
            telegramWebApp.ready();
            telegramWebApp.expand();
          } catch (initError) {
            console.warn('⚠️ Failed to initialize WebApp:', initError);
          }
          
          await new Promise(resolve => setTimeout(resolve, 200));
          
          const telegramUser = telegramWebApp.initDataUnsafe?.user;
          if (telegramUser?.id) {
            tgUserId = String(telegramUser.id);
            console.log('📱 Telegram user ID found via initDataUnsafe.user:', tgUserId);
          }
          
          if (!tgUserId && telegramWebApp.initData && telegramWebApp.initData.length > 0) {
            try {
              const urlParams = new URLSearchParams(telegramWebApp.initData);
              const userJson = urlParams.get('user');
              if (userJson) {
                const user = JSON.parse(userJson);
                if (user?.id) {
                  tgUserId = String(user.id);
                  console.log('📱 Telegram user ID found in initData string:', tgUserId);
                }
              }
            } catch (parseError) {
              console.warn('⚠️ Failed to parse initData:', parseError);
            }
          }
        }
      } catch (error: any) {
        console.warn('⚠️ Failed to access Telegram WebApp API:', error.message);
      }
      
      // Method 2: Fallback to URL parameter
      if (!tgUserId) {
        const urlParams = new URLSearchParams(window.location.search);
        tgUserId = urlParams.get('tg_user_id');
        
        if (tgUserId) {
          console.log('📱 Telegram user ID found in URL parameter:', tgUserId);
          
          if (window.history.replaceState) {
            const newUrl = window.location.pathname + (window.location.search.replace(/[?&]tg_user_id=\d+/, '').replace(/^&/, '?') || '');
            window.history.replaceState({}, '', newUrl);
            console.log('🧹 Cleaned URL, new URL:', newUrl);
          }
        }
      }
      
      // Method 3: Check sessionStorage
      if (!tgUserId) {
        const storedTgUserId = sessionStorage.getItem('tg_user_id');
        if (storedTgUserId) {
          tgUserId = storedTgUserId;
          console.log('📱 Found tg_user_id in sessionStorage (from previous load):', tgUserId);
        }
      }
      
      if (tgUserId) {
        sessionStorage.setItem('tg_user_id', tgUserId);
        console.log('💾 Saved tg_user_id to sessionStorage:', tgUserId);
      } else {
        console.log('ℹ️ No Telegram user ID found (not opened from Telegram or no user data)');
      }
    };
    
    getTelegramUserId();
  }, []);

  // Link Telegram user to Firebase user when both are available
  useEffect(() => {
    const linkTelegramUser = async () => {
      const tgUserId = sessionStorage.getItem('tg_user_id');
      
      console.log('🔗 Checking Telegram link', { 
        hasTgUserId: !!tgUserId, 
        tgUserId, 
        hasUser: !!user, 
        userUid: user?.uid,
        loading: loading
      });
      
      if (loading) {
        console.log('⏳ Auth still loading, waiting...');
        return;
      }
      
      if (tgUserId && user) {
        const linkedKey = `tg_linked_${user.uid}`;
        const alreadyLinked = sessionStorage.getItem(linkedKey);
        if (alreadyLinked) {
          console.log('✅ Telegram user already linked (cached)', { tgUserId, uid: user.uid });
          return;
        }

        try {
          console.log('🔗 Attempting to link Telegram user', { tgUserId, uid: user.uid });
          const token = await user.getIdToken();
          console.log('🔑 Got Firebase token, calling API...');
          
          const apiUrl = import.meta.env.DEV
            ? 'http://localhost:5002/astrobaby-ai/europe-west1'
            : 'https://europe-west1-astrobaby-ai.cloudfunctions.net';
          
          const fullUrl = `${apiUrl}/linkTelegramUser`;
          console.log('📡 Calling linkTelegramUser API:', fullUrl);
          
          const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ telegramUserId: tgUserId })
          });

          console.log('📥 Received response:', { status: response.status, statusText: response.statusText });
          
          const data = await response.json();
          console.log('🔗 linkTelegramUser response', { 
            status: response.status, 
            success: data.success, 
            error: data.error,
            message: data.message,
            tgUserId,
            uid: user.uid
          });
          
          if (data.success) {
            console.log('✅ Telegram user linked successfully', { tgUserId, uid: user.uid });
            sessionStorage.setItem(linkedKey, 'true');
          } else {
            console.warn('⚠️ Failed to link Telegram user', { error: data.error, tgUserId, uid: user.uid });
            
            if (data.error && !data.error.includes('already linked')) {
              toast.error('⚠️ Ошибка связывания', {
                description: data.error || 'Не удалось связать Telegram аккаунт. Попробуйте открыть приложение из бота еще раз.',
                duration: 5000,
              });
            }
          }
        } catch (error: any) {
          console.error('❌ Error linking Telegram user', { 
            error: error.message, 
            stack: error.stack,
            tgUserId, 
            uid: user?.uid 
          });
        }
      }
    };

    linkTelegramUser();
  }, [user, loading]);

  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout | null = null;

    // Функция для обновления токена в cookie
    const updateAuthCookie = async (user: User) => {
      try {
        const token = await user.getIdToken();
        // Устанавливаем cookie с максимальным временем жизни 1 час
        // Firebase токены обычно живут 1 час
        const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
        document.cookie = `auth_token=${token}; path=/; max-age=3600; SameSite=Lax${isSecure ? '; Secure' : ''}`;
      } catch (error) {
        console.error('Failed to set auth cookie:', error);
      }
    };

    // Listen to auth state changes
    const unsubscribe = authHelpers.onAuthStateChanged(async (user) => {
      // Очищаем предыдущий интервал если есть
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }

      if (user) {
        const provider = getProviderName(user);
        
        // Устанавливаем cookie для middleware
        await updateAuthCookie(user);
        
        // Обновляем токен каждые 50 минут (токены живут 1 час)
        tokenRefreshInterval = setInterval(() => {
          updateAuthCookie(user);
        }, 50 * 60 * 1000);
        
        const isRegistration = user.metadata.creationTime === user.metadata.lastSignInTime;
        const isNewUser = previousUserIdRef.current !== user.uid;
        
        if (isRegistration && isNewUser) {
          const visitNumber = trackVisit();
          console.log(`📝 Tracking registration for user ${user.uid.substring(0, 8)}... (provider: ${provider}, visit #${visitNumber})`);
          userTrackingApi.trackRegistration(user.uid, provider, user.metadata.creationTime, visitNumber)
            .then(() => {
              console.log(`✅ Registration tracking completed for user ${user.uid.substring(0, 8)}...`);
              resetVisitCount();
            })
            .catch(err => console.error('❌ Failed to track registration:', err));
        } else if (!isRegistration && isNewUser) {
          console.log(`📝 Login detected for user ${user.uid.substring(0, 8)}... (provider: ${provider}) - tracking disabled`);
        } else {
          console.log(`ℹ️ Skipping tracking for user ${user.uid.substring(0, 8)}... (isRegistration: ${isRegistration}, isNewUser: ${isNewUser})`);
        }
        
        previousUserIdRef.current = user.uid;
      } else {
        // Удаляем cookie при выходе
        document.cookie = 'auth_token=; path=/; max-age=0';
        previousUserIdRef.current = null;
      }
      
      setUser(user);
      setLoading(false);
    });

    // Check for redirect result on mount (for OAuth redirects)
    authHelpers.getRedirectResult()
      .then((result) => {
        if (result?.user) {
          const user = result.user;
          const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
          
          if (isNewUser) {
            const visitNumber = trackVisit();
            const provider = getProviderName(user);
            console.log(`📝 Tracking registration from redirect for user ${user.uid.substring(0, 8)}... (provider: ${provider}, visit #${visitNumber})`);
            userTrackingApi.trackRegistration(user.uid, provider, user.metadata.creationTime, visitNumber)
              .then(() => {
                console.log(`✅ Registration tracking completed for user ${user.uid.substring(0, 8)}...`);
                resetVisitCount();
              })
              .catch(err => console.error('❌ Failed to track registration:', err));
            
            if (typeof window !== 'undefined' && window.ym) {
              window.ym(104391687, 'reachGoal', 'registration', { method: provider.toLowerCase() });
            }

            trackAmplitudeEvent('Sign Up', { method: provider.toLowerCase() });
          }
          
          toast.success(isNewUser ? t('auth.success.signedUp') : t('auth.success.signedIn'), {
            description: isNewUser ? t('auth.welcome') : t('auth.welcomeBack'),
          });
        }
      })
      .catch((error) => {
        if (error?.code === 'auth/no-auth-event') {
          return;
        }
        
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
      });

    return () => {
      unsubscribe();
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [t]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      await authHelpers.signIn(email, password);
      toast.success(t('auth.success.signedIn'), {
        description: t('auth.welcomeBack'),
      });
    } catch (error: any) {
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      userTrackingApi.trackAuthError('Email', error?.message || errorMessage, error?.code)
        .catch(err => console.error('Failed to track auth error:', err));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const userCredential = await authHelpers.signUp(email, password);
      const user = userCredential.user;
      
      trackEvent('registration', { method: 'email' });
      trackAmplitudeEvent('Sign Up', { method: 'email' });
      
      const visitNumber = trackVisit();
      userTrackingApi.trackRegistration(
        user.uid,
        'Email',
        user.metadata.creationTime,
        visitNumber
      )
        .then(() => {
          console.log('✅ Registration tracking completed');
          resetVisitCount();
        })
        .catch(err => {
          console.error('❌ Failed to track registration:', err);
        });
      
      toast.success(t('auth.success.signedUp'), {
        description: t('auth.welcome'),
      });
    } catch (error: any) {
      const errorCode = error?.code;
      
      if (errorCode === 'auth/email-already-in-use') {
        toast.error(t('auth.errors.emailInUse'), {
          description: t('auth.errors.accountExistsUseSocial'),
        });
      } else {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
      }
      
      userTrackingApi.trackAuthError('Email', error?.message || 'unknown', error?.code)
        .catch(err => console.error('Failed to track auth error:', err));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const result = await authHelpers.signInWithGoogle();
      
      if (!result) {
        return;
      }
      
      const userCredential = result;
      const isNewUser = userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime;
      
      if (isNewUser) {
        if (typeof window !== 'undefined' && window.ym) {
          window.ym(104391687, 'reachGoal', 'registration', { method: 'google' });
        }

        trackAmplitudeEvent('Sign Up', { method: 'google' });
        
        const visitNumber = trackVisit();
        userTrackingApi.trackRegistration(
          userCredential.user.uid,
          'Google',
          userCredential.user.metadata.creationTime,
          visitNumber
        )
          .then(() => {
            console.log('✅ Registration tracking completed');
            resetVisitCount();
          })
          .catch(err => {
            console.error('❌ Failed to track registration:', err);
          });
      }
      
      toast.success(isNewUser ? t('auth.success.signedUp') : t('auth.success.signedIn'), {
        description: isNewUser ? t('auth.welcome') : t('auth.welcomeBack'),
      });
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
        userTrackingApi.trackAuthError('Google', error?.message || errorMessage, error?.code)
          .catch(err => console.error('Failed to track auth error:', err));
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithYandex = async () => {
    try {
      setLoading(true);
      await authHelpers.signInWithYandex();
      // Yandex uses redirect, so this will redirect the page
      // The callback page will handle the rest
    } catch (error: any) {
      setLoading(false);
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      userTrackingApi.trackAuthError('Yandex', error?.message || errorMessage, error?.code)
        .catch(err => console.error('Failed to track auth error:', err));
      throw error;
    }
  };

  const signInWithVK = async () => {
    try {
      setLoading(true);
      await authHelpers.signInWithVK();
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
        userTrackingApi.trackAuthError('VK', error?.message || errorMessage, error?.code)
          .catch(err => console.error('Failed to track auth error:', err));
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await authHelpers.signOut();
      // Удаляем cookie при выходе
      document.cookie = 'auth_token=; path=/; max-age=0';
      toast.success(t('auth.success.signedOut'));
    } catch (error: any) {
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      throw error;
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      await authHelpers.sendPasswordReset(email);
      toast.success(t('auth.passwordResetEmailSent'), {
        description: t('auth.passwordResetEmailDescription'),
      });
    } catch (error: any) {
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      throw error;
    }
  };

  const setupPassword = async (email: string) => {
    try {
      await authHelpers.sendPasswordReset(email);
      toast.success(t('auth.passwordSetupEmailSent'), {
        description: t('auth.passwordSetupEmailDescription'),
      });
    } catch (error: any) {
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      throw error;
    }
  };

  const updateEmail = async (email: string) => {
    try {
      await authHelpers.updateEmail(email);
    } catch (error: any) {
      console.warn('Failed to update email in Firebase:', error);
    }
  };

  const linkWithGoogle = async () => {
    try {
      await authHelpers.linkWithGoogle();
      toast.success(t('auth.success.providerLinked'), {
        description: t('auth.google') + ' ' + t('auth.linkAccount'),
      });
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
      }
      throw error;
    }
  };

  const linkWithYandex = async () => {
    try {
      await authHelpers.linkWithYandex();
      toast.success(t('auth.success.providerLinked'), {
        description: t('auth.yandex') + ' ' + t('auth.linkAccount'),
      });
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
      }
      throw error;
    }
  };

  const linkWithVK = async () => {
    try {
      await authHelpers.linkWithVK();
      toast.success(t('auth.success.providerLinked'), {
        description: t('auth.vk') + ' ' + t('auth.linkAccount'),
      });
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        const errorMessage = authHelpers.parseAuthError(error);
        toast.error(t('auth.errors.unknownError'), {
          description: t(errorMessage),
        });
      }
      throw error;
    }
  };

  const unlinkProvider = async (providerId: string) => {
    try {
      await authHelpers.unlinkProvider(providerId);
      toast.success(t('auth.success.providerUnlinked'));
    } catch (error: any) {
      const errorMessage = authHelpers.parseAuthError(error);
      toast.error(t('auth.errors.unknownError'), {
        description: t(errorMessage),
      });
      throw error;
    }
  };

  const getLinkedProviders = () => {
    return authHelpers.getLinkedProviders();
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithYandex,
    signInWithVK,
    signOut,
    sendPasswordReset,
    setupPassword,
    updateEmail,
    linkWithGoogle,
    linkWithYandex,
    linkWithVK,
    unlinkProvider,
    getLinkedProviders,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
